import type { RefObject } from 'react'
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib'
import { useAvatarPresentation } from '@/components/VRMViewerV2/hooks/useAvatarPresentation'
import { useCamera } from '@/components/VRMViewerV2/hooks/useCamera'
import { useExpressions } from '@/components/VRMViewerV2/hooks/useExpressions'
import { useInitialMotionPreparation } from '@/components/VRMViewerV2/hooks/useInitialMotionPreparation'
import { useVrmaMotion } from '@/components/VRMViewerV2/hooks/useVrmaMotion'
import { useVrmLoader } from '@/components/VRMViewerV2/hooks/useVrmLoader'
import { useVrmModel } from '@/components/VRMViewerV2/hooks/useVrmModel'
import type { MotionSequence } from '@/components/VRMViewerV2/types'

type VRMModelProps = {
  avatarFile: string
  motionSequence: MotionSequence
  controlsRef: RefObject<OrbitControlsImpl | null>
  onLoadResult?: (url: string, success: boolean) => void
}
export const VRMModel = ({ avatarFile, motionSequence, controlsRef, onLoadResult }: VRMModelProps) => {
  const gltfLoader = useVrmLoader()
  const { initialMotionPrepared, resetInitialMotionPreparation, onVrmLoaded, initialMotionHandlers } =
    useInitialMotionPreparation()

  const { vrm, mixerRef } = useVrmModel({
    avatarFile,
    gltfLoader,
    onBeforeLoad: resetInitialMotionPreparation,
    onVrmLoaded,
    onLoadResult,
  })
  const { activeMotion, activeMotionReady } = useVrmaMotion({
    vrm,
    mixerRef,
    gltfLoader,
    motionSequence,
    initialMotionHandlers,
  })
  useExpressions({
    vrm,
    expressions: activeMotion?.avatarPresentation.expressions,
    readyToApply: initialMotionPrepared && activeMotionReady,
  })
  useCamera({
    controlsRef,
    cameraPresentation: activeMotion?.cameraPresentation,
    restartKey: motionSequence.restartKey,
  })
  const groupRef = useAvatarPresentation({
    avatarPresentation: activeMotion?.avatarPresentation,
    restartKey: motionSequence.restartKey,
  })

  if (!vrm || !initialMotionPrepared) return null
  return (
    <group ref={groupRef}>
      <primitive object={vrm.scene} dispose={null} />
    </group>
  )
}
